import { createContext, useContext, useEffect, useState } from "react";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem("jh_cart");
      return saved ? JSON.parse(saved) : { restaurant: null, items: [] };
    } catch { return { restaurant: null, items: [] }; }
  });

  useEffect(() => {
    localStorage.setItem("jh_cart", JSON.stringify(cart));
  }, [cart]);

  const addItem = (restaurant, item) => {
    setCart((prev) => {
      // New restaurant? Reset cart.
      if (prev.restaurant && prev.restaurant.id !== restaurant.id) {
        return { restaurant, items: [{ ...item, quantity: 1 }] };
      }
      const existing = prev.items.find((i) => i.id === item.id);
      if (existing) {
        return {
          restaurant,
          items: prev.items.map((i) => i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i),
        };
      }
      return { restaurant, items: [...prev.items, { ...item, quantity: 1 }] };
    });
  };

  const updateQty = (itemId, qty) => {
    setCart((prev) => ({
      ...prev,
      items: qty <= 0
        ? prev.items.filter((i) => i.id !== itemId)
        : prev.items.map((i) => i.id === itemId ? { ...i, quantity: qty } : i),
    }));
  };

  const removeItem = (itemId) => updateQty(itemId, 0);

  const clearCart = () => setCart({ restaurant: null, items: [] });

  // Replace the entire cart at once (used by "re-order")
  const replaceCart = (restaurant, items) =>
    setCart({ restaurant, items: items.map((i) => ({ ...i, quantity: i.quantity || 1 })) });

  const subtotal = cart.items.reduce((s, i) => s + i.price * i.quantity, 0);
  const itemCount = cart.items.reduce((n, i) => n + i.quantity, 0);

  return (
    <CartContext.Provider value={{ cart, addItem, updateQty, removeItem, clearCart, replaceCart, subtotal, itemCount }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be inside CartProvider");
  return ctx;
};
